import { Icon } from "@iconify/react";
import { ContentWrapper } from "../../../../../Components/Layout/ContentWrapper";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { api } from "../../../../../../SPA/Services/api.service";
import { panelApiUrl } from "~/Utils";
import { useNotification } from "../../../../../Hooks/useNotification";
import { useQuery } from "../../../../../Hooks/useQuery";
import { useHasPanelAccess } from "../../../../../Hooks/useHasAccess";
import { getFormattedDate } from "../../../../../Utils/CommonUtils";
import { useEffect, useState } from "react";
import { categoryApi, regionApi, toArray, uniqueCategories } from "../../../../../Services";

/** One row of the regional pricing table. */
interface PriceRow {
  key: string;
  regionId: string;
  regionName: string;
  itemValue: string;
  /** The region's own copy of the section's category, which the rate is saved under. */
  categoryId?: string;
  /** Picked in the modal; not part of the SOR payload, which carries no currency. */
  currencyCode?: string;
  updatedBy?: string;
  updatedOn?: string;
}

/** One "Add Category" section: the category and its rate in each region. */
interface CategorySection {
  categoryId: string;
  prices: PriceRow[];
}

interface FormValues {
  sorId: string;
  itemName: string;
  uom: string;
  description: string;
  categories: CategorySection[];
}

const isNumber = (value: any) => String(value).trim() !== "" && !isNaN(Number(value)) && Number(value) >= 0;
const byName = (key: string) => (a: any, b: any) => String(a[key] ?? "").localeCompare(String(b[key] ?? ""));
const categoryLabel = (category: any) =>
  category?.categoryCode ? `${category.categoryName} (${category.categoryCode})` : category?.categoryName ?? "";

/**
 * Add / Edit / View SOR ID.
 *
 * SOR ID Details (SOR ID, Item Name, Description, UOM) and "Add Category".
 * Each category added opens its own section — "Category - <name>" over that
 * category's regional pricing: one row per region, added with "Add" and
 * edited or removed with the row's pencil and bin.
 */
export const AddNewSORId = () => {
  const { openNotification } = useNotification();
  const navigate = useNavigate();
  const query = useQuery();
  const { getPanelUser } = useHasPanelAccess();
  const id = query.get("id");
  const mode = query.get("mode");
  const isView = mode === "view";
  const [recordData, setRecordData] = useState<any>({});
  const [existingSorIds, setExistingSorIds] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(!!id);
  const [categories, setCategories] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  /* The pricing row being added (no key yet) or edited, and whose section it is. */
  const [priceDraft, setPriceDraft] = useState<{ section: number; row: PriceRow } | null>(null);

  useEffect(() => {
    categoryApi
      .getAll()
      .then((rows: any) => setCategories(toArray(rows).sort(byName("categoryName"))))
      .catch(() => setCategories([]));
    regionApi
      .getAll()
      .then((rows: any) => setRegions(toArray(rows).sort(byName("regionName"))))
      .catch(() => setRegions([]));
  }, []);

  /* One option per category name; a section is keyed by its first copy's id. */
  const categoryGroups = uniqueCategories(categories);
  const groupOf = (categoryId: string) => categoryGroups.find((group) => group.ids.includes(categoryId));

  /* A saved item names its category by id, code or name, and its prices by
     region id or name — read whichever it carries. */
  const categoryIdOf = (entry: any) =>
    entry?.categoryId ??
    categories.find(
      (category: any) =>
        (entry?.categoryCode && category.categoryCode === entry.categoryCode) ||
        (entry?.categoryName && category.categoryName === entry.categoryName)
    )?.id ??
    "";
  const pricesOf = (entry: any, entryIndex: number): PriceRow[] =>
    toArray(entry?.regionPrices).map((price: any, index: number) => {
      const region = regions.find((r: any) => r.id === price.regionId || r.regionName === price.regionName);
      return {
        key: `saved-${entryIndex}-${index}`,
        regionId: region?.id ?? price.regionId ?? "",
        regionName: region?.regionName ?? price.regionName ?? "",
        itemValue: String(price.itemValue ?? ""),
        categoryId: categoryIdOf(entry),
        updatedBy: price.updatedBy ?? recordData?.updatedBy ?? recordData?.createdBy,
        updatedOn: price.updatedOn ?? recordData?.updatedOn ?? recordData?.createdOn,
      };
    });
  /* Every category section saved: `categories` when the item has several, or
     the single top-level category an older item carries. Copies of one
     category (one per region) come back as a single section. */
  const savedSections: CategorySection[] = (
    toArray(recordData?.categories).length
      ? toArray(recordData?.categories)
      : recordData?.categoryId || recordData?.categoryCode || recordData?.categoryName
      ? [recordData]
      : []
  ).reduce<CategorySection[]>((sections, entry: any, entryIndex: number) => {
    const categoryId = groupOf(categoryIdOf(entry))?.id ?? categoryIdOf(entry);
    const section = sections.find((saved) => saved.categoryId === categoryId);
    if (section) section.prices.push(...pricesOf(entry, entryIndex));
    else sections.push({ categoryId, prices: pricesOf(entry, entryIndex) });
    return sections;
  }, []);

  const fetchExistingSorIds = () => {
    api
      .get(`${panelApiUrl}/sorItem/get-sor-list?page=0&size=1000`)
      .then((res: any) => {
        const records = res?.data?.response?.data?.records || [];
        setExistingSorIds(records);
      })
      .catch((err: any) => console.log(err));
  };

  const getRecords = () => {
    setIsLoading(true);
    api
      .get(`${panelApiUrl}/sorItem/${id}`)
      .then((res: any) => {
        setRecordData(res?.data?.response?.data);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchExistingSorIds();
    if (id) {
      getRecords();
    } else {
      setIsLoading(false);
    }
  }, [id]);

  const backToList = () => navigate("/workorder-manage-catalog?activeTab=sorId");

  return (
    <>
      <ContentWrapper
        customTitle={
          <Space align="start">
            <Icon
              onClick={backToList}
              icon="akar-icons:arrow-left"
              style={{
                fontSize: "1.8rem",
                cursor: "pointer",
              }}
            />
            <span>
              <Typography.Title
                level={3}
                style={{ margin: 0 }}
              >
                {isView ? "View SOR ID" : id ? "Edit SOR ID" : "Add SOR ID"}
              </Typography.Title>
            </span>
          </Space>
        }
      >
        <Typography.Title level={4}>SOR ID Details</Typography.Title>
        {isLoading ? (
          <Row
            justify="center"
            align="middle"
            style={{ minHeight: 300 }}
          >
            <Spin size="large" />
          </Row>
        ) : (
          <Formik<FormValues>
            enableReinitialize
            validateOnMount
            initialValues={{
              sorId: recordData?.sorId ?? "",
              itemName: recordData?.itemName ?? "",
              uom: recordData?.uom ?? "",
              description: recordData?.description ?? "",
              categories: savedSections,
            }}
            validate={(values) => {
              const errors: Record<string, string> = {};
              const spaceErr = "Leading and trailing spaces are not allowed";
              const hasEdgeSpaces = (str: any) => typeof str === "string" && /^\s|\s$/.test(str);

              if (!values.sorId) {
                errors.sorId = "SOR ID is required";
              } else if (hasEdgeSpaces(values.sorId)) {
                errors.sorId = spaceErr;
              } else if (
                existingSorIds.find(
                  (item: any) => String(item.sorId).toLowerCase() === String(values.sorId).toLowerCase() && item.id !== id
                )
              ) {
                errors.sorId = "This SOR ID already exists";
              }

              if (!values.itemName) {
                errors.itemName = "Item Name is required";
              } else if (hasEdgeSpaces(values.itemName)) {
                errors.itemName = spaceErr;
              }

              if (!values.uom) errors.uom = "UOM is required";
              else if (hasEdgeSpaces(values.uom)) errors.uom = spaceErr;

              /* Optional; only checked for stray edge spaces when given. */
              if (values.description && hasEdgeSpaces(values.description)) errors.description = spaceErr;

              if (!values.categories.length) errors.categories = "Add at least one category";
              else if (values.categories.some((section) => !section.prices.length))
                errors.categories = "Add a rate for at least one region in each category";
              return errors;
            }}
            onSubmit={(formValues) => {
              /* A section's rates go back under each region's own copy of the category. */
              const sections = formValues.categories.flatMap((section) =>
                Array.from(new Set(section.prices.map((price) => price.categoryId || section.categoryId))).map(
                  (categoryId) => {
                    const category = categories.find((entry: any) => entry.id === categoryId);
                    return {
                      categoryId,
                      categoryCode: category?.categoryCode ?? "",
                      categoryName: category?.categoryName ?? "",
                      regionPrices: section.prices
                        .filter((price) => (price.categoryId || section.categoryId) === categoryId)
                        .map((price) => ({
                          regionId: price.regionId,
                          regionName: price.regionName,
                          itemValue: Number(price.itemValue),
                        })),
                    };
                  }
                )
              );
              /* Exactly the shape the SOR API takes — nothing else alongside. */
              const values = {
                sorId: formValues.sorId,
                itemName: formValues.itemName,
                uom: formValues.uom,
                description: formValues.description,
                categories: sections,
              };
              const request = id
                ? api.put(`${panelApiUrl}/sorItem/${id}`, values)
                : api.post(`${panelApiUrl}/sorItem`, values);

              request
                .then((res: any) => {
                  openNotification({
                    title: id ? "Updated SOR ID" : "Added SOR ID",
                    message: res?.data?.message,
                    type: "success",
                  });
                  backToList();
                })
                .catch((err: any) => {
                  openNotification({
                    title: id ? "Failed to update SOR" : "Failed to create SOR",
                    message: err?.message,
                    type: "error",
                  });
                });
            }}
          >
            {({ handleSubmit, handleChange, handleBlur, values, touched, errors, isValid, setFieldValue, setFieldTouched, submitCount }) => {
              const showErrors = submitCount > 0 || !!touched.categories;
              const nameOf = (categoryId: string) =>
                categories.find((category: any) => category.id === categoryId)?.categoryName ?? "Category";
              const setSections = (next: CategorySection[]) => {
                setFieldValue("categories", next);
                setFieldTouched("categories", true, false);
              };

              const savePrice = (section: number, row: PriceRow) => {
                const user = getPanelUser();
                const stamped = {
                  ...row,
                  regionName: regions.find((region: any) => region.id === row.regionId)?.regionName ?? row.regionName,
                  updatedBy: user?.panelUserName ?? user?.userName ?? user?.email,
                  updatedOn: new Date().toISOString(),
                };
                setSections(
                  values.categories.map((entry, index) =>
                    index !== section
                      ? entry
                      : {
                          ...entry,
                          prices: row.key
                            ? entry.prices.map((price) => (price.key === row.key ? stamped : price))
                            : [...entry.prices, { ...stamped, key: `new-${Date.now()}` }],
                        }
                  )
                );
                setPriceDraft(null);
              };

              const priceColumns = (section: number): any[] => [
                { title: "Region", dataIndex: "regionName" },
                {
                  title: "Item Value",
                  dataIndex: "itemValue",
                  render: (value: any, row: any) =>
                    `${!row.currencyCode || row.currencyCode === "MYR" ? "RM" : row.currencyCode} ${Number(value).toLocaleString()}`,
                },
                { title: "Last Updated By", dataIndex: "updatedBy", render: (value: any) => value || "-" },
                {
                  title: "Last Updated At",
                  dataIndex: "updatedOn",
                  render: (value: any) => getFormattedDate(value) || "-",
                },
                ...(isView
                  ? []
                  : [
                      {
                        title: "Action",
                        dataIndex: "action",
                        width: 90,
                        render: (_value: any, row: PriceRow) => (
                          <Space size={16}>
                            <Icon
                              icon="mdi:pencil-outline"
                              role="button"
                              aria-label={`Edit ${row.regionName}`}
                              style={{ cursor: "pointer", fontSize: 18, color: "#29256E" }}
                              onClick={() => setPriceDraft({ section, row })}
                            />
                            <Icon
                              icon="fluent:delete-48-regular"
                              role="button"
                              aria-label={`Delete ${row.regionName}`}
                              style={{ cursor: "pointer", fontSize: 18, color: "#ff4d4f" }}
                              onClick={() =>
                                setSections(
                                  values.categories.map((entry, index) =>
                                    index === section
                                      ? { ...entry, prices: entry.prices.filter((price) => price.key !== row.key) }
                                      : entry
                                  )
                                )
                              }
                            />
                          </Space>
                        ),
                      },
                    ]),
              ];
              const draftSection = priceDraft ? values.categories[priceDraft.section] : undefined;

              return (
                <Form
                  layout="vertical"
                  onFinish={handleSubmit}
                >
                  <div style={{ width: "70%" }}>
                    <Row gutter={24}>
                      <Col
                        xs={24}
                        sm={12}
                      >
                        <Form.Item
                          required
                          label="SOR ID"
                          validateStatus={touched.sorId && errors.sorId ? "error" : undefined}
                          help={touched.sorId && errors.sorId ? errors.sorId : undefined}
                        >
                          <Input
                            name="sorId"
                            value={values.sorId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            readOnly={isView}
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        xs={24}
                        sm={12}
                      >
                        <Form.Item
                          required
                          label="Item Name"
                          validateStatus={touched.itemName && errors.itemName ? "error" : undefined}
                          help={touched.itemName && errors.itemName ? errors.itemName : undefined}
                        >
                          <Input
                            name="itemName"
                            value={values.itemName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            readOnly={isView}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      label="Description"
                      validateStatus={touched.description && errors.description ? "error" : undefined}
                      help={touched.description && errors.description ? errors.description : undefined}
                    >
                      <Input.TextArea
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        rows={4}
                        readOnly={isView}
                      />
                    </Form.Item>
                    <Row gutter={24}>
                      <Col
                        xs={24}
                        sm={12}
                      >
                        <Form.Item
                          required
                          label="UOM"
                          validateStatus={touched.uom && errors.uom ? "error" : undefined}
                          help={touched.uom && errors.uom ? errors.uom : undefined}
                        >
                          <Input
                            name="uom"
                            value={values.uom}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            readOnly={isView}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Figma: no Category field on the page — "Add Category"
                        opens a section per category, each with its own pricing. */}
                    {!isView && (
                      <Row
                        justify="end"
                        style={{ marginTop: 8 }}
                      >
                        <Col style={{ textAlign: "right" }}>
                          <Button
                            type="primary"
                            onClick={() => setCategoryModalOpen(true)}
                          >
                            <Space>
                              <Icon
                                icon="akar-icons:plus"
                                inline
                              />
                              Add Category
                            </Space>
                          </Button>
                          {showErrors && !values.categories.length && errors.categories && (
                            <Typography.Text
                              type="danger"
                              style={{ display: "block", marginTop: 4 }}
                            >
                              {String(errors.categories)}
                            </Typography.Text>
                          )}
                        </Col>
                      </Row>
                    )}

                    {values.categories.map((section, sectionIndex) => (
                      <div key={section.categoryId}>
                        <Divider
                          dashed
                          style={{ margin: "20px 0", borderColor: "#bfbfbf" }}
                        />
                        <Row
                          justify="space-between"
                          align="middle"
                          style={{ marginBottom: 16 }}
                        >
                          <Col>
                            <Typography.Title
                              level={5}
                              style={{ margin: 0 }}
                            >
                              Category - {nameOf(section.categoryId)}
                            </Typography.Title>
                          </Col>
                          {!isView && (
                            <Col>
                              <Space size={16}>
                                <Button
                                  type="primary"
                                  onClick={() =>
                                    setPriceDraft({
                                      section: sectionIndex,
                                      row: { key: "", regionId: "", regionName: "", itemValue: "" },
                                    })
                                  }
                                  disabled={section.prices.length >= regions.length && regions.length > 0}
                                >
                                  <Space>
                                    <Icon
                                      icon="akar-icons:plus"
                                      inline
                                    />
                                    Add
                                  </Space>
                                </Button>
                                <Icon
                                  icon="fluent:delete-48-regular"
                                  role="button"
                                  aria-label={`Remove ${nameOf(section.categoryId)}`}
                                  style={{ cursor: "pointer", fontSize: 20, color: "#ff4d4f" }}
                                  onClick={() => setSections(values.categories.filter((_entry, index) => index !== sectionIndex))}
                                />
                              </Space>
                            </Col>
                          )}
                        </Row>
                        <Table
                          bordered={false}
                          pagination={false}
                          rowKey="key"
                          dataSource={section.prices}
                          columns={priceColumns(sectionIndex)}
                          style={{ border: "1px solid #f0f0f0", borderRadius: 8 }}
                        />
                        {showErrors && !section.prices.length && (
                          <Typography.Text
                            type="danger"
                            style={{ display: "block", marginTop: 8 }}
                          >
                            Add a rate for at least one region
                          </Typography.Text>
                        )}
                      </div>
                    ))}

                    {!isView && (
                      <Row
                        justify="end"
                        gutter={16}
                        style={{ marginTop: 32 }}
                      >
                        <Col>
                          <Button onClick={backToList}>Cancel</Button>
                        </Col>
                        <Col>
                          <Button
                            type="primary"
                            htmlType="submit"
                            disabled={!isValid}
                            onClick={() => setFieldTouched("categories", true, false)}
                          >
                            Submit
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </div>

                  {/* A region is priced once per category. */}
                  <PriceModal
                    draft={priceDraft?.row ?? null}
                    categoryIds={draftSection && (groupOf(draftSection.categoryId)?.ids ?? [draftSection.categoryId])}
                    taken={(draftSection?.prices ?? []).map((price) => price.regionId)}
                    onCancel={() => setPriceDraft(null)}
                    onSave={(row) => priceDraft && savePrice(priceDraft.section, row)}
                  />
                  {/* Only categories not already added. */}
                  <CategoryModal
                    open={categoryModalOpen}
                    categories={categoryGroups.filter(
                      (category) => !values.categories.some((section) => section.categoryId === category.id)
                    )}
                    onCancel={() => setCategoryModalOpen(false)}
                    onSave={(categoryId) => {
                      setSections([...values.categories, { categoryId, prices: [] }]);
                      setCategoryModalOpen(false);
                    }}
                  />
                </Form>
              );
            }}
          </Formik>
        )}
      </ContentWrapper>
    </>
  );
};

/** Add or edit one region's rate. */
const PriceModal = ({
  draft,
  categoryIds,
  taken,
  onCancel,
  onSave,
}: {
  draft: PriceRow | null;
  /** The section's category, every copy of it: only the regions it is offered in are listed. */
  categoryIds?: string[];
  /** Regions already priced in this section. */
  taken: string[];
  onCancel: () => void;
  onSave: (row: PriceRow) => void;
}) => {
  const [regionId, setRegionId] = useState("");
  const [itemValue, setItemValue] = useState("");
  const [tried, setTried] = useState(false);

  /* GET /region/by-category for each copy, read each time the modal opens. */
  const [categoryRegions, setCategoryRegions] = useState<any[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  /* The ids as one string: the array is rebuilt on every render. */
  const categoryKey = categoryIds?.join(",") ?? "";
  useEffect(() => {
    if (!draft || !categoryKey) return;
    setCategoryRegions([]);
    setLoadingRegions(true);
    regionApi
      .byCategories(categoryKey.split(","))
      .then(setCategoryRegions)
      .finally(() => setLoadingRegions(false));
  }, [draft, categoryKey]);
  const regions = categoryRegions.filter((region) => region.id === draft?.regionId || !taken.includes(region.id));

  useEffect(() => {
    setRegionId(draft?.regionId ?? "");
    setItemValue(draft?.itemValue ?? "");
    setTried(false);
  }, [draft]);

  const regionError = !regionId ? "Please select a region" : undefined;
  const valueError = !String(itemValue).trim()
    ? "Please enter the item value"
    : !isNumber(itemValue)
    ? "Enter a number of 0 or more"
    : undefined;

  const save = () => {
    setTried(true);
    if (regionError || valueError || !draft) return;
    onSave({
      ...draft,
      regionId,
      regionName: regions.find((region) => region.id === regionId)?.regionName ?? draft.regionName,
      categoryId: regions.find((region) => region.id === regionId)?.categoryId ?? draft.categoryId,
      itemValue: String(itemValue).trim(),
      currencyCode: "MYR",
    });
  };

  return (
    <Modal
      title={draft?.key ? "Edit Details" : "Add Details"}
      open={!!draft}
      onCancel={onCancel}
      maskClosable={false}
      centered
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={save}
          >
            Submit
          </Button>
        </div>
      }
    >
      <Form layout="vertical">
        <Form.Item
          required
          label="Region"
          validateStatus={tried && regionError ? "error" : undefined}
          help={tried ? regionError : undefined}
        >
          <Select
            placeholder="Select Region"
            loading={loadingRegions}
            notFoundContent={loadingRegions ? undefined : "No more regions for this category"}
            value={regionId || undefined}
            onChange={(value: string) => setRegionId(value)}
            showSearch
            optionFilterProp="children"
          >
            {regions.map((region: any) => (
              <Select.Option
                key={region.id}
                value={region.id}
              >
                {region.regionName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          required
          label="Item Value"
          validateStatus={tried && valueError ? "error" : undefined}
          help={tried ? valueError : undefined}
        >
          {/* RM MYR only, for now — not a choice. */}
          <Input
            addonBefore="RM MYR"
            value={itemValue}
            onChange={(e) => setItemValue(e.target.value)}
            placeholder="Enter Value"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/** "Add Category": pick a category from the catalog to add a section for. */
const CategoryModal = ({
  open,
  categories,
  onCancel,
  onSave,
}: {
  open: boolean;
  categories: any[];
  onCancel: () => void;
  onSave: (categoryId: string) => void;
}) => {
  const [picked, setPicked] = useState<string>();

  useEffect(() => {
    if (open) setPicked(undefined);
  }, [open]);

  return (
    <Modal
      title="Add Category Details"
      open={open}
      onCancel={onCancel}
      maskClosable={false}
      centered
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            disabled={!picked}
            onClick={() => picked && onSave(picked)}
          >
            Submit
          </Button>
        </div>
      }
    >
      <Form layout="vertical">
        <Form.Item label="Category">
          <Select
            placeholder="Select Category"
            value={picked}
            onChange={setPicked}
            showSearch
            optionFilterProp="children"
          >
            {categories.map((category: any) => (
              <Select.Option
                key={category.id}
                value={category.id}
              >
                {categoryLabel(category)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};
